import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MixDesignService } from 'src/app/shared/services/mix-design.service';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';

@Component({
    selector: 'app-mix-design-search-result',
    templateUrl: './mix-design-search-result.component.html',
    styleUrls: ['./mix-design-search-result.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MixDesignSearchResultComponent {
  searchResult: any[] = [];
  mixDesignCode: string = '';
  originalMixReference: string = '';
  mixDesigns: any[] = [];
  displayedColumns = [
    'combinedInfo',
    'action',
  ];
  selectedTabIndex = 0;
  innerDisplayedColumns = [
    'sampleId',
    'projectCreatedDate',
    'averageMoisture',
    'averageWetTr',
    'averageDryTr',
    'averageDensity'
  ];
  hasQueryMixReference = false;

  dataSource = new MatTableDataSource<any>();
  groupedData: any[] = [];
  expandedGroups: number[] = [];
  errorMessage: string = '';

  constructor(
    public mixDesignService: MixDesignService,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title 
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      var mixDesignCode = params.get('mixDesignCode') ?? '';
      var mixReference = params.get('mixReference') ?? '';

      this.hasQueryMixReference = !!mixReference && mixReference !== 'null';
      this.mixDesignCode = mixDesignCode;
      this.originalMixReference = mixReference;
      this.titleService.setTitle(mixDesignCode);
      this.getMixDesignSearchResults(mixDesignCode, mixReference);
      this.getMixDesign(mixDesignCode, mixReference);
    });
  }

  getMixDesign(mixDesignCode: string, mixReference?: string) {
    this.mixDesignService.getMixDesign(mixDesignCode).subscribe({
      next: (mixDesign: any) => {
        const results = Array.isArray(mixDesign) ? mixDesign : [mixDesign];
        const transformedMixes: any[] = [];

        results.forEach(md => {
          if (md.items && Array.isArray(md.items)) {
            md.items = this.sortItemsByMaterialType(md.items);
          }

          transformedMixes.push(md);
          if (md.geothermMixReference) {
            const refMix = md.geothermMixReference;
            if (refMix.items && Array.isArray(refMix.items)) {
              refMix.items = this.sortItemsByMaterialType(refMix.items);
            }

            refMix.isReference = true;
            transformedMixes.push(refMix);
          }
        });

        this.mixDesigns = transformedMixes;
        if (mixReference) {
          const index = this.mixDesigns.findIndex(
            x => x.mixReferenceId?.toString() === mixReference
          );

          this.selectedTabIndex = index >= 0 ? index : 0;
        } else {
          this.selectedTabIndex = 0;
        }
      },
      error: (err: any) => {
        console.error('Error fetching mix design search results:', err);
        this.errorMessage = 'Error fetching mix design search results';
      }
    });
  }

  openEditMixDesign(mixDesign: any) {
    let route: string[];

    if (!mixDesign.isReference) {
      route = ['/mix-design/client/edit', mixDesign.id];
    } else {
      route = ['/mix-design/geotherm/edit', mixDesign.id];
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(route)
    );

    window.open(url, '_blank');
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '';
    return DateTimeUtils.getLocalDateTimeFromUTCDate(dateString, 'MMM d, yyyy').toString();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
    const selectedMix = this.mixDesigns[event.index];

    if (selectedMix) {
      this.getMixDesignSearchResults(selectedMix.mixDesignCode, selectedMix.mixReferenceId);
    }
  }

  getMixDesignSearchResults(mixDesignCode: string, mixReference: string) {
    this.mixDesignService.getMixDesignSearchResults(mixDesignCode, mixReference).subscribe({
      next: (mixDesignResults: any) => {
        const results = Array.isArray(mixDesignResults) ? mixDesignResults : [mixDesignResults];
        this.dataSource.data = results;
        this.groupDataByGroupIndex(results);
        this.expandedGroups = [];
      },
      error: (err: any) => {
        console.error('Error fetching mix design search results:', err);
        this.errorMessage = 'Error fetching mix design search results';
      }
    });
  }

  private groupDataByGroupIndex(data: any[]) {
    const groupMap = new Map<number, any[]>();

    data.forEach(item => {
      if (!groupMap.has(item.groupIndex)) {
        groupMap.set(item.groupIndex, []);
      }
      groupMap.get(item.groupIndex)!.push(item);
    });

    this.groupedData = Array.from(groupMap.values()).map(group => {
      const subGroupMap = new Map<number, any[]>();

      group.forEach(item => {
        if (!subGroupMap.has(item.projectId)) {
          subGroupMap.set(item.projectId, []);
        }
        subGroupMap.get(item.projectId)!.push(item);
      });

      return {
        groupIndex: group[0].groupIndex,
        firstItem: group[0],
        subGroups: Array.from(subGroupMap.values()).map(sub => sub)
      };
    });
  }

  toggleGroup(groupIndex: number): void {
    const index = this.expandedGroups.indexOf(groupIndex);
    if (index === -1) {
      this.expandedGroups.push(groupIndex);
    } else {
      this.expandedGroups.splice(index, 1);
    }
  }

  openSample(item: any) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/precast-worksheet`], {
        queryParams: {
          soilType: 'precast',
          projectId: item.projectId
        }
      })
    );
    window.open(url, '_blank');
  }

  getWaterCementRatio(items: any[]): number {
    let totalWater = 0;
    let totalCementitious = 0;

    items.forEach((item: any) => {
      const weight = +item.weight || 0;
      const type = item.materialType?.toLowerCase() || '';

      if (type.includes('water')) totalWater += weight;
      if (type.includes('cement')) totalCementitious += weight;
    });

    return totalCementitious
      ? +(totalWater / totalCementitious).toFixed(2)
      : 0;
  }

  getMoistureContent(items: any[]): number {
    let totalWeight = 0;
    let totalWater = 0;

    items.forEach((item: any) => {
      const type = item.materialType?.toLowerCase() || '';
      if (type === 'add mixer') {
        return;
      }

      const weight = +item.weight || 0;
      totalWeight += weight;

      if (type.includes('water')) {
        totalWater += weight;
      }
    });

    const dryWeight = totalWeight - totalWater;

    return dryWeight
      ? +(((totalWater / dryWeight) * 100).toFixed(2))
      : 0;
  }

  getDryDensity(items: any[]): number {
    let totalWeight = 0;
    let totalWater = 0;

    items.forEach((item: any) => {
      const type = item.materialType?.toLowerCase() || '';
      if (type === 'add mixer') return;

      const weight = +item.weight || 0;
      totalWeight += weight;

      if (type.includes('water')) totalWater += weight;
    });

    const dryWeight = totalWeight - totalWater;

    return dryWeight
      ? +((dryWeight / 27).toFixed(2))
      : 0;
  }


  sortItemsByMaterialType(items: any[]): any[] {
    const orderMap: any = {
      'course aggregate': 1,
      'fine aggregate': 2,
      'cementitious': 3,
      'water': 4,
      'Add Mixer': 5
    };

    return items.sort((a: any, b: any) => {
      const typeA = (a.materialType || '').toLowerCase();
      const typeB = (b.materialType || '').toLowerCase();

      const orderA = orderMap[typeA] ?? 999;
      const orderB = orderMap[typeB] ?? 999;

      return orderA - orderB;
    });
  }
}
